import { getBillingMode, selectAllowUpgrade, selectStpRates } from '@va/aaas/shared/selectors';
import { AaasBillingMode } from '@va/aaas/shared/types';
import { websiteRoleSelectors } from '@va/dashboard/selectors/core';
import { Checkmark, CoinsIcon, StarV2 } from '@va/icons';
import { useLocalizationContext } from '@va/localization';
import {
  ChoiceSeparator,
  CircularProgress,
  fontWeights,
  Paragraph,
  paragraphSizes,
  TabbedButton,
} from '@va/ui/design-system';
import { addNumberSeparator } from '@va/util/helpers';
import classNames from 'classnames';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { ConsumedTouchpointRow } from './ConsumedTouchpointRow';
import { CurrentPlan } from './CurrentPlan';
import { UpgradeButton } from './UpgradeButton';
import { useWebsiteStpData } from './useWebsiteStpData';

enum TabNames {
  StpConsumed,
  StpRate,
}

export const WebsiteStpCounterContent = () => {
  const rowsData = useWebsiteStpData();
  const billingMode = useSelector(getBillingMode);
  const allowUpgrade = useSelector(selectAllowUpgrade);
  const { locale, translate } = useLocalizationContext();
  const { stpLimit, stpConsumed } = useSelector(selectStpRates);

  const [selectedTab, setSelectedTab] = useState(TabNames.StpConsumed);

  const isWebsiteContributor = useSelector(websiteRoleSelectors.isWebsiteContributor);

  const hasInfiniteCredits = useMemo(() => stpLimit === -1, [stpLimit]);
  const isStpRateTab = useMemo(() => selectedTab === TabNames.StpRate, [selectedTab]);
  const isInternalUpgradeEnabled = useMemo(() => billingMode === AaasBillingMode.PlatformManaged, [billingMode]);

  const tabButtons = useMemo(
    () => [
      { value: TabNames.StpConsumed, label: translate('label.credits') },
      { value: TabNames.StpRate, label: translate('label.howItWorks') },
    ],
    [translate],
  );

  return (
    <div className='bg-black-lighter-84 rounded-24 p-3 text-white-83'>
      <CurrentPlan label={translate('creditsCounter.websitePlan')} />
      {(allowUpgrade || isInternalUpgradeEnabled) && (
        <UpgradeButton
          disabled={isWebsiteContributor}
          tooltip={isWebsiteContributor && translate('tooltips.contributorRole')}
          label={translate('creditsCounter.upgradeWebsitePlan')}
        />
      )}
      <div>
        <TabbedButton
          buttonOptions={tabButtons}
          selectedValue={selectedTab}
          onChange={setSelectedTab}
          className='bg-black-dark! my-3'
          textClassName='text-white underline underline-offset-4'
          activeButtonClassName='bg-white-25!'
          inactiveButtonClassName='hover:bg-transparent active:bg-transparent'
          size='small'
        />
      </div>
      <div className='flex flex-row items-center gap-2.25 px-2'>
        <CoinsIcon color='#fff' />
        <div className='flex flex-col'>
          <Paragraph size={paragraphSizes.tiny} weight={fontWeights.medium}>
            {translate('pageHeader.creditsCounter.creditsLeft')}
          </Paragraph>
          <Paragraph
            colorClassName={classNames({
              'text-orange-dark': hasInfiniteCredits,
            })}
          >
            {hasInfiniteCredits
              ? '∞'
              : translate('feature.counter.counting', {
                  behaviourCount: addNumberSeparator(stpLimit - stpConsumed, locale),
                  featureLimit: addNumberSeparator(stpLimit, locale),
                })}
          </Paragraph>
        </div>
        {!hasInfiniteCredits && (
          <div className='ml-auto'>
            <CircularProgress
              radius={15}
              stroke={4}
              progress={(stpConsumed / stpLimit) * 100}
              gradientColor='#F66F1E'
            />
          </div>
        )}
      </div>
      <ChoiceSeparator
        className='my-3! gap-18px!'
        labelClassName='text-white-83 leading-18!'
        label={
          isStpRateTab
            ? translate('pageHeader.creditsCounter.howItWorks')
            : translate('pageHeader.creditsCounter.creditsConsumed')
        }
      />
      <div className='flex flex-col gap-3 px-2'>
        {rowsData.map(
          (
            {
              icon,
              stpConsumed,
              stpConsumedLabel,
              stpRate,
              stpRateLabel,
              hidden,
              stpConsumedLabelTooltip,
              stpRateLabelTooltip,
            },
            index,
          ) => (
            <ConsumedTouchpointRow
              key={index}
              label={isStpRateTab ? stpRateLabel : stpConsumedLabel}
              count={isStpRateTab ? stpRate : stpConsumed}
              icon={icon}
              labelTooltip={isStpRateTab ? stpRateLabelTooltip : stpConsumedLabelTooltip}
              rightIcon={isStpRateTab ? <CoinsIcon color='#fff' /> : undefined}
              hidden={hidden}
            />
          ),
        )}
        {!isStpRateTab && (
          <ConsumedTouchpointRow
            label={translate('featureCounter.premiumSupport')}
            rightIcon={<Checkmark color='#FFF' />}
            icon={<StarV2 color='#ffffffd4' stroke='#ffffffd4' />}
          />
        )}
      </div>
    </div>
  );
};
